import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Stack, Button, FormControl, FormLabel, FormGroup, RadioGroup, FormControlLabel, Radio, TextField, Checkbox } from '@mui/material';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import axios from 'axios';
import { APIURL } from '../../../config';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Matches the status filter used on the client screen (Customers_V2)
const CLIENT_STATUSES = ['Active', 'Inactive', 'Prospect', 'Dormant'];

const normalizeSurveyCustomers = (customers = [], companyId) => customers
    .filter((customer) => Number(customer.cus_company_id ?? customer.com_id) === Number(companyId))
    .map((customer) => ({
        ...customer,
        cus_id: customer.cus_id ?? customer.id,
        cus_name: customer.cus_name ?? customer.customer_name ?? customer.name ?? '',
    }))
    .filter((customer) => customer.cus_id !== undefined && customer.cus_id !== null);

const getCustomerStatusGroup = (status) => (
    ['Active', 'Inactive', 'Prospect'].includes(status) ? status : 'Dormant'
);

const getSurveyRecipients = (customers, mode, statuses, selectedCustomers) => (
    mode === 'customers'
        ? selectedCustomers
        : customers.filter((customer) => statuses.includes(getCustomerStatusGroup(customer.cus_status)))
);

const getSurveySelection = (mode, statuses, selectedCustomers, customers) => (
    mode === 'customers'
        ? { customer_ids: selectedCustomers.map((customer) => customer.cus_id) }
        : { customer_ids: getSurveyRecipients(customers, mode, statuses, selectedCustomers).map((customer) => customer.cus_id) }
);

const getFriendlySurveyError = (error) => {
    const message = typeof error === 'string'
        ? error
        : error?.response?.data?.message || '';
    const normalized = message.toLowerCase();

    if (normalized.includes('company_id, created_by, form_ids, statuses') && normalized.includes('expires_at')) {
        return 'This survey service still requires client statuses. Choose “By client status” to send, or contact support to enable sending to selected clients.';
    }
    if (normalized.includes('selected forms') && normalized.includes('not available')) {
        return 'The selected form is no longer available. Choose another form and try again.';
    }
    if (normalized.includes('not available for this company') || normalized.includes('no longer available')) {
        return 'One or more selected clients are no longer available. Refresh the page and select them again.';
    }
    if (normalized.includes('server error') || normalized.includes('failed to send client survey')) {
        return 'We could not send the survey because of a server error. Please try again. If the problem continues, contact support.';
    }
    return message || 'We could not send the survey. Check the form, recipients, and response due date, then try again.';
};

const SurveyRecipients = ({ mode, onModeChange, customers, selectedCustomers, onCustomersChange, disabled, children }) => (
    <>
        <FormControl component="fieldset" disabled={disabled}>
            <FormLabel component="legend">Send To *</FormLabel>
            <RadioGroup row value={mode} onChange={(event) => onModeChange(event.target.value)}>
                <FormControlLabel value="statuses" control={<Radio />} label="By client status" />
                <FormControlLabel value="customers" control={<Radio />} label="Select clients" />
            </RadioGroup>
        </FormControl>
        {mode === 'customers' ? (
            <Autocomplete
                key="survey-customers"
                multiple
                options={customers}
                value={selectedCustomers}
                onChange={(event, newValue) => onCustomersChange(newValue)}
                getOptionLabel={(customer) => customer.cus_name || `Client ${customer.cus_id}`}
                isOptionEqualToValue={(option, value) => String(option.cus_id) === String(value.cus_id)}
                noOptionsText="No Clients Found"
                disabled={disabled}
                renderInput={(params) => <TextField {...params} label="Select Clients *" variant="outlined" />}
                renderOption={(optionProps, customer) => (
                    <li {...optionProps} key={customer.cus_id}>
                        <ListItemText primary={customer.cus_name || `Client ${customer.cus_id}`} secondary={customer.cus_email || 'No email on file'} />
                    </li>
                )}
            />
        ) : children}
    </>
);

const ClientSurvey = (props) => {
    const [title, setTitle] = useState('Client Survey');
    const [formList, setFormList] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [statusList] = useState(CLIENT_STATUSES);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [recipientMode, setRecipientMode] = useState('statuses');
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const customerList = useMemo(
        () => normalizeSurveyCustomers(props.customerList, props.comp_id),
        [props.customerList, props.comp_id]
    );
    const [endDate, setEndDate] = useState(null);
    const [signature, setSignature] = useState(false);
    const [notes, setNotes] = useState('');
    const [isSending, setIsSending] = useState(false);

    const [successAlert, setSuccessAlert] = useState('');
    const [errorAlert, setErrorAlert] = useState('');

    useEffect(() => {
        setSelectedCustomers([]);
        setSelectedStatuses([]);
        let cancelled = false;
        const fetchData = async () => {
            try {
                let forms = await axios.get(APIURL + "/task_form/forms");
                if (!cancelled) setFormList(forms.data?.filter(form => (form.form_company === props.comp_id || form.public_form === true) && form.form_status !== 2) || []);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [props.comp_id]);

    const handleStatusToggle = (status) => {
        setSelectedStatuses((prev) => (
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        ));
    };

    const recipients = getSurveyRecipients(customerList, recipientMode, selectedStatuses, selectedCustomers);
    const recipientsWithEmail = recipients.filter(c => c.cus_email && String(c.cus_email).trim() !== '');
    const hasSelection = recipientMode === 'customers' ? selectedCustomers.length > 0 : selectedStatuses.length > 0;

    const sendSurvey = async () => {
        if (isSending) return;
        if (!selectedForm || !hasSelection || !endDate) {
            setErrorAlert('Select a form, choose one or more recipients, and set a response due date before sending.');
            return;
        }
        try {
            setIsSending(true);

            const response = await axios.post(APIURL + "/clientSurvey/send", {
                company_id: props.comp_id,
                company_name: props.user.company_name,
                company_logo: props.user.company_logo,
                created_by: props.user.emp_id,
                employee_name: props.user.emp_name,
                form_ids: [selectedForm.form_id],
                ...getSurveySelection(recipientMode, selectedStatuses, selectedCustomers, customerList),
                expires_at: moment(endDate).format('YYYY-MM-DD HH:mm:ss'),
                title: title,
                signature_required: signature,
                notes: notes
            });

            if (response.data.code === 200) {
                setSuccessAlert(`Client Survey sent to ${response.data.sent} of ${response.data.requested} clients${response.data.skipped > 0 ? ` (${response.data.skipped} skipped - no email)` : ''}${response.data.failed > 0 ? ` (${response.data.failed} failed)` : ''}`);
                setTitle('Client Survey');
                setSelectedForm(null);
                setSelectedStatuses([]);
                setSelectedCustomers([]);
                setEndDate(null);
                setSignature(false);
                setNotes('');
            } else {
                setErrorAlert(getFriendlySurveyError(response.data.message));
            }
        } catch (error) {
            console.error(error);
            setErrorAlert(getFriendlySurveyError(error));
        } finally {
            setIsSending(false);
        }
    };

    const handleSuccessAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSuccessAlert('');
    };

    const handleErrorAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setErrorAlert('');
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid size={props.leftColumnSize}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            Client Survey
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'gray' }}>
                            Send a selected form to an individual customer, a selected group, or customers by status.
                        </Typography>
                    </Stack>
                </Grid>
                <Grid size={props.rightColumnSize}>
                    <Stack spacing={2}>
                        <TextField
                            label="Task Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            inputProps={{ maxLength: 50 }}
                            variant="outlined"
                            fullWidth
                            required
                        />
                        <Autocomplete
                            noOptionsText='No Forms Found'
                            value={selectedForm}
                            onChange={(event, newValue) => {
                                setSelectedForm(newValue);
                            }}
                            options={formList}
                            getOptionLabel={(option) => option.form_name}
                            renderInput={(params) => (
                                <TextField {...params} label="Form *" variant="outlined" />
                            )}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.form_id}>
                                        <ListItemText primary={option.form_name} secondary={option.form_description} />
                                    </li>
                                )
                            }}
                        />
                        <SurveyRecipients
                            mode={recipientMode}
                            onModeChange={(mode) => {
                                setRecipientMode(mode);
                                setSelectedStatuses([]);
                                setSelectedCustomers([]);
                            }}
                            customers={customerList}
                            selectedCustomers={selectedCustomers}
                            onCustomersChange={setSelectedCustomers}
                            disabled={isSending}
                        >
                        <FormControl component="fieldset" disabled={isSending}>
                            <FormLabel component="legend">Client Statuses *</FormLabel>
                            <FormGroup row>
                                {statusList.length === 0 && (
                                    <Typography variant="body2" sx={{ color: 'gray' }}>No Statuses Found</Typography>
                                )}
                                {statusList.map((status) => (
                                    <FormControlLabel
                                        key={status}
                                        control={
                                            <Checkbox
                                                checked={selectedStatuses.includes(status)}
                                                onChange={() => handleStatusToggle(status)}
                                            />
                                        }
                                        label={status}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                        </SurveyRecipients>
                        {hasSelection && (
                            <Typography variant="body2" sx={{ color: 'gray' }}>
                                {recipientsWithEmail.length} of {recipients.length} matching clients have an email on file and will receive the survey
                            </Typography>
                        )}
                        <LocalizationProvider dateAdapter={AdapterMoment} locale="en">
                            <DatePicker
                                label="By When The Response Is Expected"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                sx={{ width: '100%' }}
                                required
                            />
                        </LocalizationProvider>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Signature Required</FormLabel>
                            <RadioGroup
                                row
                                name="tSigReq"
                                value={signature === true ? "yes" : "no"}
                                onChange={(e) => setSignature(e.target.value === "yes")}
                            >
                                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="no" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                        <TextField
                            label="Notes"
                            onChange={(e) => setNotes(e.target.value)}
                            value={notes}
                            inputProps={{ maxLength: 1000 }}
                            variant="outlined"
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            onClick={sendSurvey}
                            disabled={selectedForm === null || !hasSelection || endDate === null || isSending}
                        >
                            {isSending ? 'Sending...' : `Send Client Survey${recipientsWithEmail.length > 0 ? ` (${recipientsWithEmail.length})` : ''}`}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
            <Snackbar open={successAlert != ''} autoHideDuration={5000} onClose={handleSuccessAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"success"} elevation={6} variant="filled" onClose={handleSuccessAlertClose}>
                    {successAlert}
                </MuiAlert>
            </Snackbar>
            <Snackbar open={errorAlert != ''} autoHideDuration={5000} onClose={handleErrorAlertClose}>
                <MuiAlert sx={{ width: '100%' }} severity={"error"} elevation={6} variant="filled" onClose={handleErrorAlertClose}>
                    {errorAlert}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
}

export default ClientSurvey;
